package cbconnectit.portfolio.web.pages.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import cbconnectit.portfolio.web.components.SectionTitle
import cbconnectit.portfolio.web.components.TestimonialCard
import cbconnectit.portfolio.web.data.models.domain.Testimonial
import cbconnectit.portfolio.web.navigation.Navigation
import cbconnectit.portfolio.web.utils.Constants
import cbconnectit.portfolio.web.utils.Identifiers.AttributeName.style
import cbconnectit.portfolio.web.utils.Identifiers.PropertyName.gridAutoRows
import cbconnectit.portfolio.web.utils.Identifiers.PropertyName.gridRowEnd
import cbconnectit.portfolio.web.utils.Identifiers.PropertyName.gridRowGap
import cbconnectit.portfolio.web.utils.Identifiers.TestimonialSectionClasses.content
import cbconnectit.portfolio.web.utils.Identifiers.TestimonialSectionClasses.grid
import cbconnectit.portfolio.web.utils.Identifiers.TestimonialSectionClasses.item
import com.materialkobweb.components.Spacer
import com.varabyte.kobweb.compose.css.GridEntry
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridAutoRows
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.scrollMargin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.Element
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import org.w3c.dom.get
import kotlin.math.ceil

@Composable
fun TestimonialSection(testimonials: List<Testimonial>) {
    val breakpoint = rememberBreakpoint()

    DisposableEffect(Unit) {
        val resizeListener: (Event) -> Unit = { recalculateGridItems() }
        val colorModeListener: (Event) -> Unit = { recalculateGridItems() }

        window.addEventListener("resize", resizeListener)
        window.addEventListener("update-color-mode", colorModeListener)

        onDispose {
            window.removeEventListener("resize", resizeListener)
            window.removeEventListener("update-color-mode", colorModeListener)
        }
    }

    LaunchedEffect(testimonials) {
        delay(250)
        resizeAllGridItems()
    }

    Column(
        modifier = Modifier
            .id(Navigation.Screen.Home.TestimonialSection.id)
            .scrollMargin(Constants.HEADER_HEIGHT.px)
            .fillMaxWidth(if (breakpoint >= Breakpoint.MD) 80.percent else 90.percent)
            .maxWidth(Constants.SECTION_WIDTH.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SectionTitle(
            modifier = Modifier
                .fillMaxWidth(),
            section = Navigation.Screen.Home.TestimonialSection,
            alignment = Alignment.CenterHorizontally,
        )

        val widthPercentage = when {
            breakpoint > Breakpoint.MD -> 30.percent
            breakpoint == Breakpoint.MD -> 48.percent
            else -> 100.percent
        }

        Spacer(Modifier.height(25.px))

        Div(
            Modifier
                .classNames(grid)
                .fillMaxWidth()
                .display(DisplayStyle.Grid)
                .gridTemplateColumns {
                    repeat(GridEntry.Repeat.Auto.Type.AutoFill) {
                        minmax(widthPercentage, 1.fr)
                    }
                }
                .gap(24.px)
                .gridAutoRows {
                    size(12.px)
                }
                .toAttrs()
        ) {
            testimonials.forEach {
                TestimonialCard(testimonial = it)
            }
        }
    }
}

private fun recalculateGridItems() {
    CoroutineScope(Dispatchers.Default).launch {
        delay(100)
        resizeAllGridItems()
    }
}

/**
 * Resize the grid items to use the specific height. Inspiration taken from
 *
 * https://codepen.io/andybarefoot/pen/QMeZda
 * https://medium.com/@andybarefoot/a-masonry-style-layout-using-css-grid-8c663d355ebb
 */
private fun resizeAllGridItems() {
    val allItems = document.getElementsByClassName(item)
    allItems.asList().forEach {
        resizeGridItem(it)
    }
}

private fun resizeGridItem(element: Element) {
    val grid = document.getElementsByClassName(grid)[0]
    if (grid != null) {
        val rowHeight = window.getComputedStyle(grid).getPropertyValue(gridAutoRows).substringBefore("px")
        val rowGap = window.getComputedStyle(grid).getPropertyValue(gridRowGap).substringBefore("px").toDoubleOrNull() ?: 0.0
        val some = element.querySelector(".$content")?.getBoundingClientRect()?.height?.plus(rowGap)
        val other = rowHeight.toDouble().plus(rowGap)
        val rowSpan = ceil(some?.div(other) ?: 0.0)

        val attributes = element.getAttribute(style)
        if (attributes != null) {
            val realEnding = if (attributes.contains(gridRowEnd)) {
                val index = attributes.indexOf(gridRowEnd)
                attributes.removeRange(index, attributes.length)
            } else attributes

            element.setAttribute(style, "$realEnding$gridRowEnd: span $rowSpan;")
        }
    }
}
